import type { PrescriptionDrug, Action } from "../../../@types";
import type { AppState } from "../../../@types/context";
import { saveBeneDrugs } from "../../../api";
import { deduplicatePrescriptions } from "../../../helpers/beneficiaryInfoHelpers";
import { logError, type ApiError } from "../../../helpers/errors";
import { hasGlobalSession } from "../../../helpers/loginHelpers";
import * as TypeGuards from "../../../@types/guards";

export const updatePrescriptions = async ({
  prescriptions,
  state,
}: {
  prescriptions: PrescriptionDrug[];
  state: AppState;
}): Promise<void> => {
  if (hasGlobalSession(state)) {
    (async () => {
      try {
        await saveBeneDrugs(prescriptions);
      } catch (e) {
        logError(
          "Failed to save beneficiary drugs (AppStore/updatePrescriptions)",
          e as ApiError
        );
      }
    })();
  }
};

export const setPrescriptions = (
  state: AppState,
  { payload }: Action
): AppState => {
  let prescriptions = TypeGuards.hasPrescriptions(payload)
    ? payload
    : state.prescriptions;

  prescriptions = deduplicatePrescriptions(prescriptions);
  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions({ prescriptions, state });

  return { ...state, prescriptions };
};

export const addPrescription = (
  state: AppState,
  { payload: prescription }: Action
): AppState => {
  const prescriptions = TypeGuards.isPrescription(prescription)
    ? [...state.prescriptions, prescription]
    : state.prescriptions;

  prescriptions.sort((a, b) => a.name.localeCompare(b.name));

  updatePrescriptions({ prescriptions, state });

  return {
    ...state,
    prescriptions,
  };
};

export const editPrescription = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.hasIndex(payload) && TypeGuards.hasDrug(payload)) {
    const { index, drug } = payload;

    const prescriptions = state.prescriptions.map((prescription, i) => {
      if (i !== index) {
        return prescription;
      }

      return {
        ...prescription,
        ...drug,
      };
    });

    updatePrescriptions({ prescriptions, state });

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};

export const deletePrescription = (
  state: AppState,
  { payload }: Action
): AppState => {
  if (TypeGuards.hasIndex(payload)) {
    const prescriptions = state.prescriptions.filter(
      (p, i) => i !== payload.index
    );

    updatePrescriptions({ prescriptions, state });

    return {
      ...state,
      prescriptions,
    };
  }

  return state;
};
